import React, { useState, useEffect } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Clock,
  Navigation,
  Star,
  ThumbsUp,
  ThumbsDown,
  ExternalLink,
  Share2,
  Calendar,
  Users,
  Heart
} from 'lucide-react';
import { Location, UserLocation } from '../types';
import { getCachedRoute } from '../utils/routes';
import { getImageUrl } from '../utils/images';
import { Map } from './Map';
import { useAuth } from '../context/AuthContext';
import {
  markVisited,
  updateVisitReview,
  getVisitStatus,
  getVisitStats,
  checkFavoriteStatus,
  toggleFavorite
} from '../utils/supabase';
import { getWasVerb, getPersonWord, getSatisfiedWord } from '../utils/grammar';
import toast from 'react-hot-toast';

interface LocationProfileProps {
  location: Location;
  onClose: () => void;
  userLocation?: UserLocation | null;
  isLoading?: boolean;
}

export const LocationProfile: React.FC<LocationProfileProps> = ({
  location,
  onClose,
  userLocation,
  isLoading
}) => {
  const { user } = useAuth();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [distance, setDistance] = useState<string | null>(null);
  const [travelTime, setTravelTime] = useState<string | null>(null);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [likes, setLikes] = useState(0);
  const [dislikes, setDislikes] = useState(0);
  const [visitStatus, setVisitStatus] = useState<{ liked: boolean | null } | null>(null);
  const [stats, setStats] = useState<{ total: number; likes: number; dislikes: number } | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  // Функция для обновления статистики
  const refreshStats = async () => {
    if (location.id) {
      try {
        const newStats = await getVisitStats(location.id);
        setStats(newStats);
        setLikes(newStats.likes);
        setDislikes(newStats.dislikes);
      } catch (error) {
        console.error('Error refreshing stats:', error);
      }
    }
  };

  // Функция для обновления статуса посещения
  const refreshVisitStatus = async () => {
    if (user && location.id) {
      try {
        const status = await getVisitStatus(location.id);
        setVisitStatus(status);
      } catch (error) {
        console.error('Error refreshing visit status:', error);
      }
    }
  };

  useEffect(() => {
    if (stats) {
      setLikes(stats.likes);
      setDislikes(stats.dislikes);
    }
  }, [stats]);

  useEffect(() => {
    if (user && location.id) {
      checkFavoriteStatus(location.id)
        .then(setIsFavorite)
        .catch(console.error);
    }
  }, [user, location.id]);

  useEffect(() => {
    if (userLocation && location.coordinates) {
      getCachedRoute(userLocation, location.coordinates)
        .then(({ distance, duration }) => {
          setDistance(String(distance));
          if (duration > 60) {
            const hours = duration / 60;
            const roundedHours = Math.round(hours * 10) / 10;
            setTravelTime(
              `${Number.isInteger(roundedHours) ? Math.floor(roundedHours) : roundedHours} г.`
            );
          } else {
            setTravelTime(`${duration} хв`);
          }
        })
        .catch(console.error);
    }
  }, [userLocation, location.coordinates]);

  useEffect(() => {
    refreshStats();
    refreshVisitStatus();
  }, [user, location.id]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % location.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + location.images.length) % location.images.length);
  };

  const handleFavoriteClick = async () => {
    if (!user) {
      toast.error('Каб дадаць у абранае, калі ласка, увайдзіце ў сістэму');
      return;
    }

    if (!location.id) {
      toast.error('Памылка: няправільны ідэнтыфікатар лакацыі');
      return;
    }

    try {
      setIsTogglingFavorite(true);
      const { error } = await toggleFavorite(location.id, isFavorite);
      
      if (error) throw error;

      setIsFavorite(!isFavorite);
      toast.success(isFavorite ? 'Выдалена з абранага' : 'Дададзена ў абранае');
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Не атрымалася змяніць статус абранага');
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  const handleVisit = async () => {
    if (!user) {
      toast.error('Каб адзначыць, калі ласка, увайдзіце ў сістэму');
      return;
    }
    if (!location.id) return;
    
    try {
      const { error } = await markVisited(location.id);
      if (!error) {
        await refreshVisitStatus();
        await refreshStats();
        toast.success('Адзначана як наведанае');
      }
    } catch (error) {
      console.error('Error marking visit:', error);
      toast.error('Не атрымалася адзначыць наведванне');
    }
  };

  const handleReview = async (liked: boolean) => {
    if (!location.id) return;
    
    try {
      const { error } = await updateVisitReview(location.id, liked);
      if (!error) {
        await refreshVisitStatus();
        await refreshStats();
        toast.success('Водгук захаваны');
      }
    } catch (error) {
      console.error('Error updating review:', error);
      toast.error('Не атрымалася захаваць водгук');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: location.name,
          text: location.shortDescription,
          url: window.location.href,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Спасылка скапіравана');
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl mx-4 animate-pulse overflow-hidden">
          <div className="h-80 bg-gray-200" />
          <div className="p-8 space-y-6">
            <div className="h-8 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="h-32 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  const satisfactionRate = stats && stats.total > 0 ? Math.round((stats.likes / stats.total) * 100) : 0;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm">
        
        {/* Mobile Layout */}
        <div className="lg:hidden h-full overflow-y-auto">
          <div className="min-h-full bg-white">
            
            {/* Mobile Header */}
            <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
              <div className="flex items-center justify-between p-4">
                <h1 className="text-lg font-bold text-gray-900 truncate flex-1 mr-4">
                  {location.name}
                </h1>
                <div className="flex items-center gap-2">
                  {/* Favorite Button */}
                  {user && (
                    <button
                      onClick={handleFavoriteClick}
                      disabled={isTogglingFavorite}
                      className={`p-2 rounded-full transition-all duration-200 ${
                        isFavorite 
                          ? 'bg-red-500 text-white hover:bg-red-600' 
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-red-500'
                      } ${isTogglingFavorite ? 'opacity-50' : 'hover:scale-110'}`}
                    >
                      <Heart
                        size={16}
                        className={isFavorite ? 'fill-current' : ''}
                      />
                    </button>
                  )}
                  
                  {/* Share Button */}
                  <button
                    onClick={handleShare}
                    className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-primary rounded-full transition-all duration-200 hover:scale-110"
                  >
                    <Share2 size={16} />
                  </button>
                  
                  {/* Close Button */}
                  <button
                    onClick={onClose}
                    className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 rounded-full transition-all duration-200 hover:scale-110"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Image Gallery */}
            <div className="relative group">
              <div
                className="aspect-[16/10] bg-gradient-to-br from-gray-100 to-gray-200 cursor-pointer overflow-hidden"
                onClick={() => setIsGalleryOpen(true)}
              >
                {location.images[currentImageIndex] && (
                  <img
                    src={getImageUrl(location.images[currentImageIndex])}
                    alt={location.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              
              {/* Image Navigation */}
              {location.images.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      prevImage();
                    }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm hover:bg-white text-gray-800 p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      nextImage();
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm hover:bg-white text-gray-800 p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm font-medium">
                    {currentImageIndex + 1} / {location.images.length}
                  </div>
                </>
              )}
            </div>

            {/* Content */}
            <div className="p-4 space-y-6">
              {/* Meta Information */}
              <div className="flex flex-wrap items-center gap-4 text-sm">
                {location.coordinates && (
                  <button
                    onClick={() =>
                      navigator.clipboard.writeText(`${location.coordinates.lat}, ${location.coordinates.lng}`)
                    }
                    className="flex items-center text-primary hover:text-primary/80 transition-colors group"
                    title="Скапіраваць каардынаты"
                  >
                    <MapPin className="w-4 h-4 mr-1.5" />
                    <span className="font-mono text-xs group-hover:underline">
                      {location.coordinates.lat.toFixed(4)}, {location.coordinates.lng.toFixed(4)}
                    </span>
                  </button>
                )}
                
                {distance && (
                  <div className="flex items-center text-gray-600">
                    <Navigation className="w-4 h-4 mr-1.5 text-primary" />
                    <span className="font-medium">{distance} км</span>
                  </div>
                )}
                
                {travelTime && (
                  <div className="flex items-center text-gray-600">
                    <Clock className="w-4 h-4 mr-1.5 text-primary" />
                    <span className="font-medium">{travelTime}</span>
                  </div>
                )}
              </div>

              {/* Stats */}
              {stats && stats.total > 0 && (
                <div className="grid grid-cols-3 gap-3 p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl border border-primary/20">
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-900">{stats.total}</div>
                    <div className="text-xs text-gray-600">{getWasVerb(stats.total)} тут</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-600">{stats.likes}</div>
                    <div className="text-xs text-gray-600">спадабалася</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-yellow-600">{satisfactionRate}%</div>
                    <div className="text-xs text-gray-600">{getSatisfiedWord()}</div>
                  </div>
                </div>
              )}

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {location.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-primary/10 hover:bg-primary/20 text-primary px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 hover:scale-105 border border-primary/20 hover:border-primary/30 cursor-default"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Description */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Апісанне</h3>
                <div className="prose prose-gray max-w-none">
                  {location.description
                    .split('\n\n')
                    .map((paragraph, index) => (
                      <p key={index} className="text-gray-700 leading-relaxed mb-3 last:mb-0 text-sm">
                        {paragraph}
                      </p>
                    ))}
                </div>
              </div>

              {/* User Actions */}
              {user && (
                <div className="space-y-3">
                  {!visitStatus && (
                    <button
                      onClick={handleVisit}
                      className="w-full px-4 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors shadow-lg"
                    >
                      Я тут быў
                    </button>
                  )}
                  
                  {visitStatus && visitStatus.liked === null && (
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <p className="text-gray-900 font-medium mb-3 text-sm">Ці спадабалася вам гэтае месца?</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleReview(true)}
                          className="flex-1 px-3 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors text-sm"
                        >
                          Спадабалася
                        </button>
                        <button
                          onClick={() => handleReview(false)}
                          className="flex-1 px-3 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors text-sm"
                        >
                          Не спадабалася
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {visitStatus && visitStatus.liked !== null && (
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className={`font-medium text-sm ${visitStatus.liked ? 'text-green-600' : 'text-red-600'}`}>
                          {visitStatus.liked ? 'Спадабалася' : 'Не спадабалася'}
                        </span>
                        <button 
                          onClick={() => handleReview(visitStatus.liked ? false : true)} 
                          className="text-primary hover:text-primary/80 underline font-medium text-sm"
                        >
                          Змяніць
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Map and Navigation */}
              <div className="space-y-4">
                <div className="h-[50vh] rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                  <Map center={[location.coordinates.lat, location.coordinates.lng]} />
                </div>
                
                <div className="space-y-2">
                  <button
                    onClick={() =>
                      window.open(
                        `https://yandex.by/maps/?ll=${location.coordinates.lng},${location.coordinates.lat}&z=15&pt=${location.coordinates.lng},${location.coordinates.lat}`,
                        '_blank'
                      )
                    }
                    className="w-full flex items-center justify-center px-4 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-medium transition-all duration-200 shadow-lg"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Яндекс Навігатар
                  </button>
                  
                  <button
                    onClick={() =>
                      window.open(
                        `https://www.google.com/maps/dir//${location.coordinates.lat},${location.coordinates.lng}`,
                        '_blank'
                      )
                    }
                    className="w-full flex items-center justify-center px-4 py-3 bg-white hover:bg-gray-50 text-gray-700 rounded-xl font-medium transition-all duration-200 border border-gray-200 shadow-sm"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Google Maps
                  </button>
                </div>
              </div>

              {/* Bottom padding for safe scrolling */}
              <div className="h-8"></div>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:flex h-full items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-7xl h-[95vh] overflow-hidden relative flex flex-col">
            
            {/* Desktop Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-primary/5 to-primary/10 flex-shrink-0">
              <h2 className="text-2xl font-bold text-gray-900 truncate flex-1 mr-6">
                {location.name}
              </h2>
              <div className="flex items-center gap-3">
                {/* Favorite Button */}
                {user && (
                  <button
                    onClick={handleFavoriteClick}
                    disabled={isTogglingFavorite}
                    className={`p-2.5 rounded-full transition-all duration-200 ${
                      isFavorite 
                        ? 'bg-red-500 text-white hover:bg-red-600' 
                        : 'bg-white hover:bg-gray-50 text-gray-700 hover:text-red-500 border border-gray-200'
                    } ${isTogglingFavorite ? 'opacity-50' : 'hover:scale-110'} shadow-sm`}
                  >
                    <Heart
                      size={18}
                      className={isFavorite ? 'fill-current' : ''}
                    />
                  </button>
                )}
                
                {/* Share Button */}
                <button
                  onClick={handleShare}
                  className="p-2.5 bg-white hover:bg-gray-50 text-gray-700 hover:text-primary rounded-full transition-all duration-200 hover:scale-110 border border-gray-200 shadow-sm"
                >
                  <Share2 size={18} />
                </button>
                
                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="p-2.5 bg-white hover:bg-gray-50 text-gray-700 hover:text-gray-900 rounded-full transition-all duration-200 hover:scale-110 border border-gray-200 shadow-sm"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="flex flex-1 min-h-0">
              {/* Left Side - Content */}
              <div className="flex-1 overflow-y-auto">
                
                {/* Image Gallery */}
                <div className="relative group">
                  <div
                    className="aspect-[16/10] bg-gradient-to-br from-gray-100 to-gray-200 cursor-pointer overflow-hidden"
                    onClick={() => setIsGalleryOpen(true)}
                  >
                    {location.images[currentImageIndex] && (
                      <img
                        src={getImageUrl(location.images[currentImageIndex])}
                        alt={location.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  
                  {/* Image Navigation */}
                  {location.images.length > 1 && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          prevImage();
                        }}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          nextImage();
                        }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">
                        {currentImageIndex + 1} / {location.images.length}
                      </div>
                    </>
                  )}
                </div>

                {/* Content */}
                <div className="p-8">
                  
                  {/* Meta Information */}
                  <div className="flex flex-wrap items-center gap-6 mb-6">
                    {location.coordinates && (
                      <button
                        onClick={() =>
                          navigator.clipboard.writeText(`${location.coordinates.lat}, ${location.coordinates.lng}`)
                        }
                        className="flex items-center text-primary hover:text-primary/80 transition-colors group"
                        title="Скапіраваць каардынаты"
                      >
                        <MapPin className="w-5 h-5 mr-2" />
                        <span className="font-mono text-sm group-hover:underline">
                          {location.coordinates.lat.toFixed(4)}, {location.coordinates.lng.toFixed(4)}
                        </span>
                      </button>
                    )}
                    
                    {distance && (
                      <div className="flex items-center text-gray-600">
                        <Navigation className="w-5 h-5 mr-2 text-primary" />
                        <span className="font-medium">{distance} км</span>
                      </div>
                    )}
                    
                    {travelTime && (
                      <div className="flex items-center text-gray-600">
                        <Clock className="w-5 h-5 mr-2 text-primary" />
                        <span className="font-medium">{travelTime}</span>
                      </div>
                    )}
                    
                    {location.created_at && (
                      <div className="flex items-center text-gray-600">
                        <Calendar className="w-5 h-5 mr-2 text-primary" />
                        <span className="text-sm">
                          Дададзена {new Date(location.created_at).toLocaleDateString('be-BY')}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  {stats && stats.total > 0 && (
                    <div className="flex items-center gap-8 p-6 bg-gradient-to-r from-primary/5 to-primary/10 rounded-2xl border border-primary/20 mb-6">
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-primary/20 rounded-full">
                          <Users className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
                          <div className="text-sm text-gray-600">{getPersonWord(stats.total)} {getWasVerb(stats.total)} тут</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-green-100 rounded-full">
                          <ThumbsUp className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-green-600">{stats.likes}</div>
                          <div className="text-sm text-gray-600">спадабалася</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-yellow-100 rounded-full">
                          <Star className="w-6 h-6 text-yellow-600 fill-current" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-yellow-600">{satisfactionRate}%</div>
                          <div className="text-sm text-gray-600">{getSatisfiedWord()}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tags */}
                  <div className="flex flex-wrap gap-3 mb-8">
                    {location.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-primary/10 hover:bg-primary/20 text-primary px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 border border-primary/20 hover:border-primary/30 cursor-default"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Description */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Апісанне</h3>
                    <div className="prose prose-gray max-w-none">
                      {location.description
                        .split('\n\n')
                        .map((paragraph, index) => (
                          <p key={index} className="text-gray-700 leading-relaxed mb-4 last:mb-0">
                            {paragraph}
                          </p>
                        ))}
                    </div>
                  </div>

                  {/* User Actions */}
                  {user && (
                    <div className="space-y-4 mb-8">
                      {!visitStatus && (
                        <button
                          onClick={handleVisit}
                          className="w-full sm:w-auto px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors shadow-lg hover:shadow-xl"
                        >
                          Я тут быў
                        </button>
                      )}
                      
                      {visitStatus && visitStatus.liked === null && (
                        <div className="p-6 bg-gray-50 rounded-2xl border border-gray-200">
                          <p className="text-gray-900 font-medium mb-4">Ці спадабалася вам гэтае месца?</p>
                          <div className="flex gap-3">
                            <button
                              onClick={() => handleReview(true)}
                              className="flex-1 px-4 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 transition-colors"
                            >
                              Спадабалася
                            </button>
                            <button
                              onClick={() => handleReview(false)}
                              className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors"
                            >
                              Не спадабалася
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {visitStatus && visitStatus.liked !== null && (
                        <div className="p-6 bg-gray-50 rounded-2xl border border-gray-200">
                          <div className="flex items-center justify-between">
                            <span className={`font-medium ${visitStatus.liked ? 'text-green-600' : 'text-red-600'}`}>
                              {visitStatus.liked ? 'Спадабалася' : 'Не спадабалася'}
                            </span>
                            <button 
                              onClick={() => handleReview(visitStatus.liked ? false : true)} 
                              className="text-primary hover:text-primary/80 underline font-medium"
                            >
                              Змяніць
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Side - Map and Navigation */}
              <div className="w-96 flex flex-col bg-gray-50 border-l border-gray-200">
                <div className="flex-1 p-6 flex flex-col min-h-0">
                  <div className="flex-1 rounded-xl overflow-hidden border border-gray-200 shadow-sm mb-6 min-h-0">
                    <Map center={[location.coordinates.lat, location.coordinates.lng]} height="100%" />
                  </div>
                  
                  <div className="space-y-3">
                    <button
                      onClick={() =>
                        window.open(
                          `https://yandex.by/maps/?ll=${location.coordinates.lng},${location.coordinates.lat}&z=15&pt=${location.coordinates.lng},${location.coordinates.lat}`,
                          '_blank'
                        )
                      }
                      className="w-full flex items-center justify-center px-4 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-medium transition-all duration-200 hover:scale-105 shadow-lg"
                    >
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Яндекс Навігатар
                    </button>
                    
                    <button
                      onClick={() =>
                        window.open(
                          `https://www.google.com/maps/dir//${location.coordinates.lat},${location.coordinates.lng}`,
                          '_blank'
                        )
                      }
                      className="w-full flex items-center justify-center px-4 py-3 bg-white hover:bg-gray-50 text-gray-700 rounded-xl font-medium transition-all duration-200 hover:scale-105 border border-gray-200 shadow-sm"
                    >
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Google Maps
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full Screen Gallery */}
      {isGalleryOpen && (
        <div className="fixed inset-0 z-60 bg-black/95 backdrop-blur-sm flex items-center justify-center">
          <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center p-4">
            <button
              onClick={() => setIsGalleryOpen(false)}
              className="absolute top-6 right-6 z-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-200 hover:scale-110"
            >
              <X className="w-6 h-6" />
            </button>
            
            <img
              src={getImageUrl(location.images[currentImageIndex])}
              alt={location.name}
              className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl"
            />
            
            {location.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-4 rounded-full transition-all duration-200 hover:scale-110"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-4 rounded-full transition-all duration-200 hover:scale-110"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white px-6 py-3 rounded-full text-lg font-medium">
                  {currentImageIndex + 1} / {location.images.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default LocationProfile;